/**
 * CountryGlobe shader - Fragment shader for country-colored globe visualization.
 *
 * Applies multiple visual effects to create an attractive globe appearance:
 * - Country textures with borders
 * - Terrain grain (paper/fabric-like texture on land)
 * - Specular highlights (glossy center reflection)
 * - Vignette effect (darkens edges)
 * - Emboss effect (saturation boost and darkening near borders)
 * - Subtle grid lines (lat/lng reference)
 */

// =============================================================================
// Visual Effect Configuration
// =============================================================================

/**
 * Configuration for country globe visual effects.
 * Adjust these values to tune the globe's appearance.
 */
export const COUNTRY_GLOBE_CONFIG = {
  // Terrain grain effect - adds paper/fabric-like texture to land areas
  grain: {
    frequency: 700.0, // Higher = finer grain pattern
    intensity: 0.18, // Higher = more visible grain texture
    offset: 0.09, // Centers grain around 0 (should be intensity/2)
  },

  // Specular highlight - creates glossy center reflection
  specular: {
    falloff: 0.8, // Higher = smaller highlight area
    land: 0.25, // Land highlight intensity (subtle)
    water: 0.5, // Water highlight intensity (shinier)
  },

  // Vignette effect - darkens edges for depth/focus
  vignette: {
    start: 0.5, // Distance from center where darkening begins
    end: 1.4, // Distance where maximum darkening is reached
    intensity: 0.5, // How much to darken at edges (0-1)
  },

  // Emboss effect - boosts saturation and darkens near country borders
  emboss: {
    saturation: 0.3, // Max saturation boost (30%)
    darken: 0.8, // How much to darken at borders (80%)
  },

  // Grid lines - subtle blue lat/lng reference lines
  grid: {
    color: [0.4, 0.5, 0.7] as const, // Subtle blue (RGB)
    opacity: 0.25, // Subtle but visible (0-1)
    lineWidth: 0.001, // Thin lines in UV space
    spacing: 0.1, // 10 lines (every 10% of UV = ~17° lat, 36° lng)
  },
} as const

// =============================================================================
// Fragment Shader
// =============================================================================

/**
 * Fragment shader for CountryGlobe component.
 *
 * Uniforms required:
 * - countryTexture: sampler2D - Country fill colors
 * - borderTexture: sampler2D - Country border lines
 * - provinceBorderTexture: sampler2D - Province/state border lines
 * - landMaskTexture: sampler2D - Land vs water mask (R channel)
 * - glowTexture: sampler2D - Glow intensity near borders (R channel)
 *
 * Varyings from vertex shader:
 * - vUv: vec2 - Texture coordinates
 * - vScreenPos: vec2 - Normalized device coordinates for screen-space effects
 */
export const countryGlobeFragmentShader = `
  uniform sampler2D countryTexture;
  uniform sampler2D borderTexture;
  uniform sampler2D provinceBorderTexture;
  uniform sampler2D landMaskTexture;
  uniform sampler2D glowTexture;
  varying vec2 vUv;
  varying vec2 vScreenPos;

  // ==========================================================================
  // Visual Effect Constants
  // Adjust these to tune the globe's appearance
  // ==========================================================================

  // Terrain grain effect - adds paper/fabric-like texture to land areas
  const float GRAIN_FREQUENCY = ${COUNTRY_GLOBE_CONFIG.grain.frequency.toFixed(1)};
  const float GRAIN_INTENSITY = ${COUNTRY_GLOBE_CONFIG.grain.intensity.toFixed(2)};
  const float GRAIN_OFFSET = ${COUNTRY_GLOBE_CONFIG.grain.offset.toFixed(2)};

  // Specular highlight - creates glossy center reflection
  const float SPECULAR_FALLOFF = ${COUNTRY_GLOBE_CONFIG.specular.falloff.toFixed(1)};
  const float SPECULAR_LAND = ${COUNTRY_GLOBE_CONFIG.specular.land.toFixed(2)};
  const float SPECULAR_WATER = ${COUNTRY_GLOBE_CONFIG.specular.water.toFixed(1)};

  // Vignette effect - darkens edges for depth/focus
  const float VIGNETTE_START = ${COUNTRY_GLOBE_CONFIG.vignette.start.toFixed(1)};
  const float VIGNETTE_END = ${COUNTRY_GLOBE_CONFIG.vignette.end.toFixed(1)};
  const float VIGNETTE_INTENSITY = ${COUNTRY_GLOBE_CONFIG.vignette.intensity.toFixed(1)};

  // Emboss effect - boosts saturation and darkens near country borders
  const float EMBOSS_SATURATION = ${COUNTRY_GLOBE_CONFIG.emboss.saturation.toFixed(1)};
  const float EMBOSS_DARKEN = ${COUNTRY_GLOBE_CONFIG.emboss.darken.toFixed(1)};

  // Grid lines - subtle blue lat/lng reference lines
  const vec3 GRID_COLOR = vec3(${COUNTRY_GLOBE_CONFIG.grid.color.join(', ')});
  const float GRID_OPACITY = ${COUNTRY_GLOBE_CONFIG.grid.opacity.toFixed(2)};
  const float GRID_LINE_WIDTH = ${COUNTRY_GLOBE_CONFIG.grid.lineWidth};
  const float GRID_SPACING = ${COUNTRY_GLOBE_CONFIG.grid.spacing.toFixed(1)};

  // ==========================================================================
  // HSL Color Space Conversion Functions
  // ==========================================================================

  // RGB to HSL conversion
  vec3 rgb2hsl(vec3 c) {
    float maxC = max(c.r, max(c.g, c.b));
    float minC = min(c.r, min(c.g, c.b));
    float l = (maxC + minC) * 0.5;

    if (maxC == minC) return vec3(0.0, 0.0, l); // achromatic

    float d = maxC - minC;
    float s = l > 0.5 ? d / (2.0 - maxC - minC) : d / (maxC + minC);

    float h;
    if (maxC == c.r) h = mod((c.g - c.b) / d, 6.0);
    else if (maxC == c.g) h = (c.b - c.r) / d + 2.0;
    else h = (c.r - c.g) / d + 4.0;
    h /= 6.0;

    return vec3(h, s, l);
  }

  // Helper for hsl2rgb
  float hue2rgb(float p, float q, float t) {
    if (t < 0.0) t += 1.0;
    if (t > 1.0) t -= 1.0;
    if (t < 1.0/6.0) return p + (q - p) * 6.0 * t;
    if (t < 0.5) return q;
    if (t < 2.0/3.0) return p + (q - p) * (2.0/3.0 - t) * 6.0;
    return p;
  }

  // HSL to RGB conversion
  vec3 hsl2rgb(vec3 c) {
    if (c.y == 0.0) return vec3(c.z);

    float q = c.z < 0.5 ? c.z * (1.0 + c.y) : c.z + c.y - c.z * c.y;
    float p = 2.0 * c.z - q;

    return vec3(
      hue2rgb(p, q, c.x + 1.0/3.0),
      hue2rgb(p, q, c.x),
      hue2rgb(p, q, c.x - 1.0/3.0)
    );
  }

  // ==========================================================================
  // Noise Functions
  // ==========================================================================

  // Simple hash function for pseudo-random noise
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  // Value noise function
  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    f = f * f * (3.0 - 2.0 * f); // Smoothstep

    float a = hash(i);
    float b = hash(i + vec2(1.0, 0.0));
    float c = hash(i + vec2(0.0, 1.0));
    float d = hash(i + vec2(1.0, 1.0));

    return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
  }

  // Multi-octave noise for more natural look
  float fbm(vec2 p) {
    float value = 0.0;
    float amplitude = 0.5;
    for (int i = 0; i < 4; i++) {
      value += amplitude * noise(p);
      p *= 2.0;
      amplitude *= 0.5;
    }
    return value;
  }

  // ==========================================================================
  // Grid Lines
  // ==========================================================================

  // Calculate grid line intensity at current UV position
  float getGridIntensity(vec2 uv) {
    // Calculate distance to nearest grid line
    float latLine = abs(fract(uv.y / GRID_SPACING + 0.5) - 0.5) * GRID_SPACING;
    float lngLine = abs(fract(uv.x / GRID_SPACING + 0.5) - 0.5) * GRID_SPACING;

    // Smooth line with antialiasing
    float latIntensity = 1.0 - smoothstep(0.0, GRID_LINE_WIDTH, latLine);
    float lngIntensity = 1.0 - smoothstep(0.0, GRID_LINE_WIDTH, lngLine);

    // Combine (max for intersections)
    return max(latIntensity, lngIntensity);
  }

  // ==========================================================================
  // Main
  // ==========================================================================

  void main() {
    vec4 countryColor = texture2D(countryTexture, vUv);
    vec4 borderColor = texture2D(borderTexture, vUv);
    float isLand = texture2D(landMaskTexture, vUv).r;

    // Apply grain texture only to land
    float grain = fbm(vUv * GRAIN_FREQUENCY) * GRAIN_INTENSITY;
    vec3 grainedColor = countryColor.rgb + (grain - GRAIN_OFFSET) * isLand;

    // === Emboss Effect: Boost saturation and darken near country borders ===
    float glowIntensity = texture2D(glowTexture, vUv).r;  // 1.0 at border, 0.0 far away
    float embossStrength = glowIntensity * isLand;

    // Apply saturation boost and darkening to grained country color
    vec3 hsl = rgb2hsl(grainedColor);

    // Extra darkening for green/yellow hues (hue 0.1-0.45) which are lighter
    // Hue: 0=red, 0.16=yellow, 0.33=green, 0.5=cyan, 0.66=blue, 0.83=magenta
    float isGreenYellow = smoothstep(0.08, 0.15, hsl.x) * (1.0 - smoothstep(0.4, 0.5, hsl.x));
    float darkenMultiplier = 1.0 + isGreenYellow * 0.5;  // 50% extra darkening for green/yellow

    hsl.y = min(1.0, hsl.y * (1.0 + EMBOSS_SATURATION * embossStrength));  // Boost saturation
    hsl.z = hsl.z * (1.0 - EMBOSS_DARKEN * darkenMultiplier * embossStrength);  // Darken (extra for green/yellow)
    vec3 embossedColor = hsl2rgb(hsl);

    // Compute specular highlight - centered on view
    float distFromCenter = length(vScreenPos);
    float falloff = 1.0 - smoothstep(0.0, SPECULAR_FALLOFF, distFromCenter);

    // Different intensity for land vs water
    float specularStrength = mix(SPECULAR_WATER, SPECULAR_LAND, isLand);
    float specular = falloff * falloff * specularStrength;

    vec3 specularContrib = vec3(specular);

    // Blend country borders over embossed color (with saturation boost applied)
    vec3 finalColor = mix(embossedColor, borderColor.rgb, borderColor.a);

    // Blend province borders (gray, zoom-dependent)
    vec4 provinceBorderColor = texture2D(provinceBorderTexture, vUv);
    finalColor = mix(finalColor, provinceBorderColor.rgb, provinceBorderColor.a);

    // Add specular highlight
    finalColor += specularContrib;

    // Vignette effect - subtle darkening at edges
    float vignetteDist = length(vScreenPos);
    float vignette = 1.0 - smoothstep(VIGNETTE_START, VIGNETTE_END, vignetteDist) * VIGNETTE_INTENSITY;
    finalColor *= vignette;

    // Grid lines - subtle blue lat/lng reference
    float gridIntensity = getGridIntensity(vUv) * GRID_OPACITY;
    finalColor = mix(finalColor, GRID_COLOR, gridIntensity);

    gl_FragColor = vec4(finalColor, 1.0);
  }
`
